import { Suspense } from "react";
import { Link, Outlet, redirect, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { Me } from "@repo/shared-types";
import { api } from "@/lib/api";
import { forgetEverything } from "@/lib/query";
import { queries } from "@/lib/queries";
import { loadSession } from "@/lib/session";
import { APP_NAME, Wordmark } from "@/components/brand";
import { TabBar } from "@/components/navigation";
import { LoadingBar, Toaster } from "@/components/toaster";
import { Button } from "@/components/ui/button";

/**
 * The session is warmed here for every page below; the layout reads it as a
 * query, so a fresh answer — the inbox's count moving — redraws the tab bar.
 */
export async function rootLoader() {
  await loadSession();
  return null;
}

/**
 * Sign-out is an action rather than a click handler so it goes through the
 * router: every copy is dropped and every loader re-runs, which is what
 * makes every page update without a reload.
 */
export async function signOutAction() {
  await api.auth.signOut();
  forgetEverything();
  return redirect("/");
}

/**
 * Who is signed in. Never waits: the root loader warmed it, and once the
 * cache is emptied the layout draws signed out until it is read again,
 * rather than holding the whole app — tab bar and all — on the read.
 */
export function useSession(): Me | null {
  return useQuery(queries.me()).data ?? null;
}

export default function Root() {
  const session = useSession();
  // The inbox reads what it shows, so its own page never shows a count: the
  // session this tab bar was drawn from may predate the reading. Invitations
  // not seen yet count beside the notifications: an invitee holds none.
  const onInbox = useLocation().pathname === "/inbox";
  const unread = onInbox ? 0 : (session?.inbox.unread ?? 0) + (session?.inbox.invitations ?? 0);

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <LoadingBar />
      {!session && (
        <header className="border-b border-border pt-[env(safe-area-inset-top)]">
          <nav className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
            <Link to="/" aria-label={APP_NAME}>
              <Wordmark />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/sign-in" className="font-medium text-muted-foreground hover:text-foreground">
                Sign in
              </Link>
              <Button asChild size="lg">
                <Link to="/sign-up">Sign up</Link>
              </Button>
            </div>
          </nav>
        </header>
      )}

      {/* Signed in, the page's own top carries its way back; the tab bar
          below carries everything else, so the page leaves room for it. */}
      <main
        className={
          session
            ? "mx-auto max-w-3xl px-4 pb-[calc(var(--tab-bar-top)+2rem)] pt-[calc(1.25rem+env(safe-area-inset-top))]"
            : "mx-auto max-w-3xl px-4 py-10"
        }
      >
        {/* A page waits here only once the cache has been emptied under it —
            signing out, a read answering 401 — and its loader is reading
            again: never on a navigation, which its loader has warmed. */}
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </main>

      {session && <TabBar session={session} unread={unread} />}
      <Toaster aboveTabBar={!!session} />
    </div>
  );
}
